<template>
  <b-container>
    <b-row>
      <b-col class="mb-3 mt-3">
        <h3>Migration</h3>
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col>
        <b-form-input
          type="text"
          placeholder="Enter search text"
          v-model="searchText"
          @keyup.enter="searchSurveys"
        />
      </b-col>
      <b-col>
        <b-button @click="searchSurveys">
          Search
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <BaseMessageBlock :messages="errors"></BaseMessageBlock>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <BaseMessageBlock :type="'Success'" :messages="messages"></BaseMessageBlock>
      </b-col>
    </b-row>
    <LoadingIndicator 
      v-if="loadingSurveys"
      topic="Survey List"
    />
    <b-row v-if="!loadingSurveys && selectedSurvey === null">
      <b-col class="mt-4">
        <b-table
          striped hover
          :items="searchedSurveys"
          :fields="surveyListFields"
          :filter="searchText"
        >
          <template v-slot:cell(actions)="data">
            <b-button @click="selectSurvey(data.item.surveyId)">Select</b-button>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <b-row v-if="selectedSurvey" class="mt-4">
      <b-col>
        <b-row>
          <b-col>
            <h4>{{selectedSurvey.name}}</h4>
          </b-col>
        </b-row>
        <LoadingIndicator 
          v-if="loading"
          topic="Survey"
        />
        <div v-else>
          <b-row>
            <b-col>
              <h5>
                <b-button v-b-toggle.project-collapse size="sm" style="height: 36px;">
                  <font-awesome-icon
                    v-if="projectVisible"
                    icon="angle-up"
                    size="2x">
                  </font-awesome-icon>
                  <font-awesome-icon
                    v-else
                    icon="angle-down"
                    size="2x">
                  </font-awesome-icon>
                </b-button> 
                Project
                <b-badge
                  v-if="validProjectSection"
                  variant="success"
                  style="float: right;">
                  Ready!
                </b-badge>
                <b-badge
                  v-else
                  variant="danger"
                  style="float: right;">
                  Action Required
                </b-badge>
              </h5>
            </b-col>
          </b-row>
          <b-collapse id="project-collapse" v-model="projectVisible">
            <b-card class="mb-4">
              <b-row>
                <b-col cols="auto" xl="6">
                  <b-form>
                    <b-row>
                      <b-col>
                        <b-form-group
                          label="Project Name"
                          label-for="project-name"
                          :state="errors.length == 0 ? null : projectForm.name != ''"
                          invalid-feedback="Project Name is required">
                          <b-form-input 
                            id="project-name"
                            v-model.trim="projectForm.name" 
                            :state="errors.length == 0 ? null : projectForm.name != ''"
                            required autofocus>
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                    </b-row>
                     <b-row>
                      <b-col>
                        <b-form-group
                          label="Survey Name"
                          label-for="survey-name"
                          :state="errors.length == 0 ? null : projectForm.surveyName != ''"
                          invalid-feedback="Survey Name is required">
                          <b-form-input 
                            id="survey-name"
                            v-model.trim="projectForm.surveyName" 
                            :state="errors.length == 0 ? null : projectForm.surveyName != ''"
                            required>
                          </b-form-input>
                        </b-form-group>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <b-form-group
                          label="Start Date"
                          label-for="project-startdate"
                          :state="errors.length == 0 ? null : projectForm.startDate != ''"
                          invalid-feedback="Start Date is required">
                          <!-- <b-form-input
                            id="project-startdate"
                            v-model.trim="projectForm.startDate"
                            :state="errors.length == 0 ? null : projectForm.startDate != ''"
                            required
                            type="date">
                          </b-form-input> -->
                          <b-form-datepicker
                            id="project-startdate"
                            v-model.trim="projectForm.startDate"
                            :state="errors.length == 0 ? null : projectForm.startDate != ''"
                            required
                            size="sm"
                            placeholder="mm/dd/yyyy"
                            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                          ></b-form-datepicker>
                        </b-form-group>
                      </b-col>
                      <b-col>
                        <b-form-group
                          label="Status"
                          label-for="project-status">
                          <b-form-select
                            id="project-status"
                            v-model.trim="projectForm.status"
                            :state="errors.length == 0 ? null : true">
                            <option value="Open">Open</option>
                            <option value="Closed">Closed</option>
                          </b-form-select>
                        </b-form-group>
                      </b-col>
                    </b-row>
                    <b-form-group
                      label="Owner"
                      label-for="project-owner"
                      :state="errors.length == 0 ? null : projectForm.owner != ''"
                      invalid-feedback="Owner is required">
                      <b-input-group>
                        <b-form-select
                          id="project-owner"
                          v-model.trim="projectForm.owner"
                          required
                          :state="errors.length == 0 ? null : projectForm.owner != ''"
                          :options="ownerOptions"
                          @change="onOwnerChange">
                        </b-form-select>
                      </b-input-group>
                    </b-form-group>
                    <b-form-group
                      label="Contractor"
                      label-for="project-contractor"
                      :state="errors.length == 0 ? null : projectForm.contractor != ''"
                      invalid-feedback="Contractor is required">
                      <b-input-group>
                        <b-form-select
                          id="project-contractor"
                          v-model.trim="projectForm.contractor"
                          required
                          :state="errors.length == 0 ? null : projectForm.contractor != ''"
                          :options="contractorOptions"
                          @change="onContractorChange">
                        </b-form-select>
                      </b-input-group>
                    </b-form-group>
                    <b-row>
                      <b-col>
                        <b-form-group
                          label="Resident Engineer"
                          label-for="project-re"
                          :state="errors.length == 0 ? null : projectForm.primaryOwner != ''"
                          invalid-feedback="Resident Engineer is required">
                          <b-input-group>
                            <b-form-select
                              id="project-re"
                              v-model.trim="projectForm.primaryOwner"
                              :state="errors.length == 0 ? null : projectForm.primaryOwner != ''"
                              :options="ownerUserOptions">
                            </b-form-select>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                      <b-col>
                        <b-form-group
                          label="Project Manager"
                          label-for="project-pm"
                          :state="errors.length == 0 ? null : projectForm.primaryContractor != ''"
                          invalid-feedback="Project Manager is required">
                          <b-input-group>
                            <b-form-select
                              id="project-pm"
                              v-model.trim="projectForm.primaryContractor"
                              :state="errors.length == 0 ? null : projectForm.primaryContractor != ''"
                              :options="contractorUserOptions">
                            </b-form-select>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-form>
                </b-col>
                <b-col></b-col>
              </b-row>
            </b-card>
          </b-collapse>
          <b-row>
            <b-col>
              <h5>
                <b-button v-b-toggle.organizations-collapse size="sm" style="height: 36px;">
                  <font-awesome-icon v-if="organizationsVisible" icon="angle-up" size="2x" />
                  <font-awesome-icon v-else icon="angle-down" size="2x" />
                </b-button> 
                Organizations ({{this.surveyMigration.organizations.length}})
                <!-- <b-badge
                  v-if="validOrganizationsSection"
                  variant="success"
                  style="float: right;"
                >
                  Ready!
                </b-badge>
                <b-badge
                  v-else
                  variant="danger"
                  style="float: right;"
                >
                  Action Required
                </b-badge> -->
              </h5>
            </b-col>
          </b-row>
          <b-collapse id="organizations-collapse" v-model="organizationsVisible">
            <b-card class="mb-4">
              <b-row>
                <b-col cols="auto" xl="8">
                  <b-table
                    striped hover
                    :items="surveyMigration.organizations"
                    :fields="organizationFields">
                    <template v-slot:cell(alreadyExists)="data">
                      <font-awesome-icon
                        v-if="data.item.alreadyExists"
                        :icon="['fas', 'check']"
                        size="1x"
                        :style="{color: '#28a745'}">
                      </font-awesome-icon>
                      <font-awesome-icon
                        v-else
                        :icon="['fas', 'times']"
                        size ="1x"
                        :style="{color: '#dc3545'}">
                      </font-awesome-icon>
                    </template>
                  </b-table>
                </b-col>
                <b-col>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
          <b-row>
            <b-col>
              <h5>
                <b-button v-b-toggle.users-collapse size="sm" style="height: 36px;" >
                  <font-awesome-icon v-if="usersVisible" icon="angle-up" size="2x" />
                  <font-awesome-icon v-else icon="angle-down" size="2x" />
                </b-button> 
                Users ({{this.surveyMigration.users.length}})
                <!-- <b-badge
                  v-if="validUsersSection"
                  variant="success"
                  style="float: right;"
                >
                  Ready!
                </b-badge>
                <b-badge
                  v-else
                  variant="danger"
                  style="float: right;"
                >
                  Action Required
                </b-badge> -->
              </h5>
            </b-col>
          </b-row>
          <b-collapse id="users-collapse" v-model="usersVisible">
            <b-card class="mb-4">
              <b-row>
                <b-col cols="auto">
                  <b-table
                    striped hover
                    :items="usersAndRelations"
                    :fields="userFields">
                    <template v-slot:cell(alreadyExists)="data">
                      <font-awesome-icon
                        v-if="data.item.alreadyExists"
                        :icon="['fas', 'check']"
                        size="1x"
                        :style="{color: '#28a745'}">
                      </font-awesome-icon>
                      <font-awesome-icon
                        v-else
                        :icon="['fas', 'times']"
                        size ="1x"
                        :style="{color: '#dc3545'}">
                      </font-awesome-icon>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
          <b-row>
            <b-col>
              <h5>
                <b-button v-b-toggle.summary-collapse size="sm" style="height: 36px;">
                  <font-awesome-icon v-if="summaryVisible" icon="angle-up" size="2x" />
                  <font-awesome-icon v-else icon="angle-down" size="2x" />
                </b-button> 
                Summary
                <b-badge
                  v-if="validSummarySection"
                  variant="success"
                  style="float: right;">
                  Ready!
                </b-badge>
                <b-badge
                  v-else
                  variant="danger"
                  style="float: right;">
                  Action Required
                </b-badge>
              </h5>
            </b-col>
          </b-row>
          <b-collapse id="summary-collapse" v-model="summaryVisible">
            <b-card class="mb-4">
              <b-row>
                <b-col cols="12" md="6">
                  <strong v-if="surveyMigration.issues.length > 0">
                    WARNING: There are issues with this survey.
                    Migrating could lead to unpredictable system behavior
                  </strong>
                  <ul>
                    <li v-for="issue in surveyMigration.issues" :key="issue">
                      {{ issue }}
                    </li>
                  </ul>
                  <b-button @click="migrate" :disabled="!validSummarySection">
                    <div style="width: 60px;" >
                      <b-spinner v-if="migrating" small label="Spinning"/>
                      <div v-else>Migrate</div>
                    </div>
                  </b-button>
                </b-col>
                <b-col cols="12" md="6">
                  <b-row v-if="surveyMigration.missingQuestions.length > 0">
                    <b-col>
                      <h5>Missing Questions</h5>
                      <b-form>
                        <b-form-group
                          v-for="(question, i) in surveyMigration.missingQuestions"
                          :key="question"
                          :label="question"
                          :label-for="'missing-question-' + i"
                          :state="errors.length == 0 ? null : questionMapping[i] !== ''"
                          invalid-feedback="Please select a question to map to">
                          <b-form-select
                            v-model="questionMapping[i]"
                            :state="errors.length == 0 ? null : questionMapping[i] !== ''"
                            :id="'missing-question-' + i"
                            :options="questionOptions"
                            required>
                          </b-form-select>
                        </b-form-group>
                      </b-form>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import axios from "axios";

export default {
  name: "Migration",
  data() {
    return {
      errors: [],
      messages: [],
      searchText: "",
      loadingSurveys: false,
      searchedSurveys: [],
      selectedSurvey: null,
      loading: false,
      surveyMigration: null,
      projectVisible: true,
      organizationsVisible: false,
      usersVisible: false,
      summaryVisible: true,
      projectForm: this.newProjectForm(),
      surveyName: "",
      sending: false,
      migrating: false,
      usersAndRelations: [],
      contractorUserOptions: [],
      ownerUserOptions: [],
      questionMapping: [],
      surveyListFields: [
        { key: "actions", label: "", thStyle: "width: 5rem" },
        { key: "name", label: "Survey Name", sortable: true },
        { key: "migrated", label: "Migrated", sortable: true, thStyle: "width: 5rem",
          tdClass: (value) => value ? "table-success" : "table-danger" }
      ],
      organizationFields: [
        { key: "name", label: "Name"},
        { key: "role", label: "Role", 
          tdClass: (value, key, item) => { return this.getRoleClass(item.role); } },
        { key: "responseCount", label: "Responses"},
        { key: "userCount", label: "Users"},
        { key: "alreadyExists", label: "In system", tdClass: 'flagClass'}
      ],
      questionFields: [
        { key: "text", label: "Question Text"}
      ],
      userFields: [
        { key: "name", label: "Name", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "organization", label: "Organization", sortable: true },
        { key: "role", label: "Role", sortable: true,
          tdClass: (value, key, item) => { return this.getRoleClass(item.role); } },
        { key: "responseCount", label: "Responses", sortable: true },
        { key: "alreadyExists", label: "In system", tdClass: 'flagClass'}
      ],
      
    }
  },
  mounted() {
    if (!this.isSystemAdmin) {
      this.defaultRedirect();
    }
    this.loadSurveys();
  },
  methods: {
    loadSurveys() {
      this.loadingSurveys = true;
      axios
        .get(`${this.$api_url}/etl/survey/list`, this.getReqConfig())
        .then(response => {
          this.searchedSurveys = response.data;
          this.searchedSurveys
            .sort((a, b) => a.name < b.name ? 1 : -1)
            .sort((a, b) => a.migrated ? b.migrated ? 0 : 1 : -1)
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.loadingSurveys = false;
        });
    },
    searchSurveys() {
      this.selectedSurvey = null;
      this.errors = [];
      this.messages = [];
    },
    selectSurvey(surveyId){
      this.resetMigration();
      this.selectedSurvey = this.searchedSurveys.find(s => s.surveyId === surveyId);
      this.loading = true;
      axios
        .get(`${this.$api_url}/etl/survey/migration/${surveyId}`, this.getReqConfig())
        .then(response => {
          this.surveyMigration = response.data;
          this.resetVisibility();
          this.loadProject(this.surveyMigration.project);
          this.loadUsersAndRelations();
          this.updateOwnerUserOptions();
          this.updateContractorUserOptions();
          this.surveyMigration.missingQuestions.forEach((mq, i) =>{
            this.questionMapping[i] = '';
          });
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getRoleClass(role) {
      if (role === "Owner") {
        return "ownerClass";
      } else if (role === "Contractor") {
        return "contractorClass";
      } else {
        return "";
      }
    },
    loadProject(project) {
      this.projectForm = {
        name: project.projectName,
        surveyName: project.projectName,
        primaryOwner: "",
        primaryContractor: "",
        startDate: this.$helpers.APIStringToInput(project.startDate),
        owner: "",
        contractor: "",
        status: project.status
      }
    },
    loadUsersAndRelations(){
      let users = [];
      if (this.surveyMigration) {
        users = this.surveyMigration.users.map(u => ({ 
          ...u
        }));
        users.forEach(u => {
          let relation = this.surveyMigration.relations.find(r => r.userId === u.userId);
          if (relation) {
            u.role = relation.userRole;
            u.organization = relation.organizationName;
            u.organizationId = relation.userOrganizationId;
          }
        })
      }
      this.usersAndRelations = users;
    },
    onOwnerChange(){
      this.projectForm.primaryOwner = "";
      this.updateOwnerUserOptions();
    },
    onContractorChange(){
      this.projectForm.primaryContractor = "";
      this.updateContractorUserOptions();
    },
    updateOwnerUserOptions() {
      let options = [{value: '', text: ''}];
      if (this.surveyMigration) {
        let users = this.usersAndRelations
          .filter(u => u.organizationId === this.projectForm.owner);
        if (this.projectForm.primaryContractor) {
          users = users.filter(u => u.userId != this.projectForm.primaryContractor);
        }
        options.push(...users.map(u => ({
          value: u.userId,
          text: u.name
        })));
      }
      this.ownerUserOptions = options;
    },
    updateContractorUserOptions() {
      let options = [{value: '', text: ''}];
      if (this.surveyMigration) {
        let users = this.usersAndRelations
          .filter(u => u.organizationId === this.projectForm.contractor);
        if (this.projectForm.primaryOwner) {
          users = users.filter(u => u.userId != this.projectForm.primaryOwner);
        }
        options.push(...users.map(u => ({
          value: u.userId,
          text: u.name
        })));
      }
      this.contractorUserOptions = options;
    },
    migrate() {
      let request = this.buildRequest();

      this.migrating = true;
      axios
        .post(`${this.$api_url}/etl/migrate`, request, this.getReqConfig())
        .then(() => {
          this.loadSurveys();
          this.searchSurveys();
          this.messages.push({ message: "Survey successfully migrated!" });
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.migrating = false;
        });
    },
    buildRequest() {
      let projectRequest = {
        projectId: this.surveyMigration.project.projectId,
        projectName: this.projectForm.name,
        ownerOrganizationId: this.projectForm.owner,
        contractorOrganizationId: this.projectForm.contractor,
        primaryOwnerUserId: this.projectForm.primaryOwner,
        primaryContractorUserId: this.projectForm.primaryContractor,
        status: this.projectForm.status,
        startDate: this.projectForm.startDate
      };

      let surveyRequest = {
        surveyId: this.surveyMigration.survey.surveyId,
        surveyName: this.projectForm.surveyName,
        projectId: this.surveyMigration.survey.projectId
      };
      
      return {
        project: projectRequest,
        survey: surveyRequest,
        ownerName: this.surveyMigration.organizations
          .find(o => o.organizationId === this.projectForm.owner).name,
        contractorName: this.surveyMigration.organizations
          .find(o => o.organizationId === this.projectForm.contractor).name,
        primaryOwnerEmail: this.surveyMigration.users
          .find(u => u.userId === this.projectForm.primaryOwner).email,
        primaryContractorEmail: this.surveyMigration.users
          .find(u => u.userId === this.projectForm.primaryContractor).email,
        questionMapping: this.surveyMigration.missingQuestions.map((qm, i) => ({
          key: qm,
          value: this.questionMapping[i]
        }))
      };
    },
    newProjectForm() {
      return {
        name: "",
        surveyName: "",
        primaryOwner: "",
        primaryContractor: "",
        startDate: "",
        owner: "",
        contractor: "",
        status: ""
      };
    },
    resetVisibility() {
      this.projectVisible = true;
      this.usersVisible = false;
      this.organizationsVisible = false;
      this.summaryVisible = true;
    },
    resetMigration() {
      this.projectForm = this.newProjectForm();
      this.surveyName = "";
      this.usersAndRelations = [];
      this.contractorUserOptions = [],
      this.ownerUserOptions = [],
      this.questionMapping = []
    },
  },
  computed: {
    responseCount() {
      return this.surveyMigration ? this.surveyMigration.responseCount : 0;
    },
    ownerOptions() {
      let options = [{value: '', text: ''}];
      if (this.surveyMigration) {
        options.push(...this.surveyMigration.organizations
          .filter(u => u.role === "Owner").map(o => ({
            value: o.organizationId,
            text: `${o.name} (${o.userCount} users)`
        })));
      }
      return options;
    },
    contractorOptions() {
      let options = [{value: '', text: ''}];
      if (this.surveyMigration) {
        options.push(...this.surveyMigration.organizations
          .filter(u => u.role === "Contractor").map(o => ({
            value: o.organizationId,
            text: `${o.name} (${o.userCount} users)`
        })));
      }
      return options;
    },
    questionOptions() {
      let options = [{value: '', text: ''}];
      if (this.surveyMigration && this.surveyMigration.missingQuestions.length > 0) {
        options.push(...this.surveyMigration.questions.map(q => ({
          value: q.text,
          text: q.text
        })));
      }
      return options;
    },
    validProjectSection(){
      return this.projectForm.name &&
        this.projectForm.surveyName &&
        this.projectForm.primaryOwner &&
        this.projectForm.primaryContractor &&
        this.projectForm.startDate &&
        this.projectForm.owner &&
        this.projectForm.contractor &&
        this.projectForm.status;
    },
    validOrganizationsSection() {
      return true;
    },
    validUsersSection() {
      return true;
    },
    validSummarySection() {
      let valid = true;
      for (var i = 0; i < this.surveyMigration.missingQuestions.length; i++) {
        if (!this.questionMapping[i]) {
          valid = false;
        }
      }

      return valid && this.validProjectSection && this.validOrganizationsSection &&
        this.validUsersSection;
    }
  }
}

</script>
<style>
.flagClass {
  text-align: center;
}
.ownerClass {
  background-color: #a4b7c5;
}
.contractorClass {
  background-color: #99df8d;
}
</style>